import { useCallback, useEffect, useRef, useState } from "react";
import {
	fetchSavedIdeas,
	fetchToggleSavedIdea,
	type PublicProjectSummary,
} from "@/features/marketplace/services/marketplace-api.service";

export interface UseSavedIdeas {
	/** Ids of the saved projects; empty until the first load succeeds. */
	savedIds: ReadonlySet<string>;
	/** The saved projects themselves, as of the last load. */
	projects: readonly PublicProjectSummary[];
	status: "loading" | "ready" | "error";
	/** Ids with a save or unsave in flight. */
	pendingIds: ReadonlySet<string>;
	/** Reads the saved list again. */
	reload: () => Promise<void>;
	/**
	 * Saves or unsaves a project. "done" when the server accepted it; "failed"
	 * when it did not, in which case nothing the caller shows has changed;
	 * "ignored" when a request for the same project was already in flight.
	 */
	toggle: (projectId: string) => Promise<"done" | "failed" | "ignored">;
}

export function useSavedIdeas(): UseSavedIdeas {
	const [savedIds, setSavedIds] = useState<ReadonlySet<string>>(new Set());
	const [projects, setProjects] = useState<readonly PublicProjectSummary[]>([]);
	const [status, setStatus] = useState<UseSavedIdeas["status"]>("loading");
	const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
	const inFlight = useRef(new Set<string>());
	const mounted = useRef(true);

	const reload = useCallback(async () => {
		try {
			const saved = await fetchSavedIdeas();
			if (!mounted.current) return;
			setSavedIds(new Set(saved.projectIds));
			setProjects(saved.projects);
			setStatus("ready");
		} catch (error) {
			console.error("[SavedIdeas] Failed to load saved ideas:", error);
			if (!mounted.current) return;
			setStatus("error");
		}
	}, []);

	useEffect(() => {
		mounted.current = true;
		void reload();
		return () => {
			mounted.current = false;
		};
	}, [reload]);

	const toggle = useCallback(
		async (projectId: string): Promise<"done" | "failed" | "ignored"> => {
			if (inFlight.current.has(projectId)) return "ignored";
			inFlight.current.add(projectId);
			setPendingIds(new Set(inFlight.current));
			try {
				const result = await fetchToggleSavedIdea(projectId);
				if (!mounted.current) return "done";
				setSavedIds(new Set(result.projectIds));
				if (!result.saved) {
					setProjects((current) => current.filter((p) => p.id !== projectId));
				}
				return "done";
			} catch (error) {
				console.error("[SavedIdeas] Failed to toggle saved idea:", error);
				return "failed";
			} finally {
				inFlight.current.delete(projectId);
				if (mounted.current) setPendingIds(new Set(inFlight.current));
			}
		},
		[],
	);

	return { savedIds, projects, status, pendingIds, reload, toggle };
}
